import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Users, DollarSign, TrendingUp, CreditCard, Copy, Check, ArrowLeft, Loader2, Calendar, ChevronRight, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { api } from '../utils/api';

const Stat = ({ icon: Icon, label, value, sublabel, tone = "primary", testId }) => (
  <div className="rounded-xl border border-border bg-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
        tone === "primary" ? "bg-primary/10 text-primary"
        : tone === "success" ? "bg-emerald-500/10 text-emerald-500"
        : tone === "warning" ? "bg-amber-500/10 text-amber-500"
        : "bg-muted text-muted-foreground"
      }`}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
    </div>
    <p className="text-2xl font-semibold tracking-tight" data-testid={`${testId}-value`}>{value}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    {sublabel && <p className="text-[11px] text-muted-foreground/70 mt-0.5">{sublabel}</p>}
  </div>
);

const AffiliateDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.getAffiliateDashboard()
      .then(setData)
      .catch(e => setError(e.message || 'Could not load dashboard'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const copyLink = () => {
    if (!data?.referral_link) return;
    navigator.clipboard.writeText(data.referral_link);
    setCopied(true);
    toast.success('Referral link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center">
          <DollarSign className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <h2 className="text-xl font-semibold mb-2">Couldn't load your dashboard</h2>
          <p className="text-sm text-muted-foreground mb-5">
            {error.includes('revoked') ? 'Your affiliate access has been revoked. Contact support if you think this is a mistake.' : error}
          </p>
          <Link to="/dashboard"><Button>Back to dashboard</Button></Link>
        </Card>
      </div>
    );
  }

  const conversionRate = data.total_referrals > 0
    ? Math.round((data.paid_referrals / data.total_referrals) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="affiliate-dashboard">
      <Helmet><title>Affiliate Dashboard — LinkMonkey</title></Helmet>

      <nav className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <Badge className="bg-emerald-500 hover:bg-emerald-600">Affiliate</Badge>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Affiliate Program</p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">Your earnings</h1>
          <p className="text-sm text-muted-foreground">
            Earn <strong>€{data.rates.monthly_per_payment}</strong> for every monthly Pro signup and <strong>€{data.rates.yearly_per_payment}</strong> for every yearly Pro signup — for as long as they stay subscribed.
          </p>
        </div>

        {/* Referral link card */}
        <Card className="p-5 mb-6 border-primary/40 bg-primary/[0.03]" data-testid="referral-link-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground mb-1">Your referral link</p>
              <p className="text-sm font-mono truncate text-foreground" data-testid="referral-link">{data.referral_link}</p>
            </div>
            <Button onClick={copyLink} variant={copied ? "outline" : "default"} size="sm" data-testid="copy-referral-link-btn">
              {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy</>}
            </Button>
          </div>
        </Card>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat icon={Users} label="Total referrals" value={data.total_referrals} testId="stat-total" />
          <Stat icon={TrendingUp} label="Paid subscribers" value={data.paid_referrals} sublabel={`${conversionRate}% conversion`} testId="stat-paid" tone="success" />
          <Stat icon={Calendar} label="Monthly subs" value={data.monthly_active} sublabel={`= €${data.monthly_recurring.toFixed(2)} /mo`} testId="stat-monthly" />
          <Stat icon={CreditCard} label="Yearly subs" value={data.yearly_active} sublabel={`= €${data.yearly_recurring.toFixed(2)} /yr`} testId="stat-yearly" />
        </div>

        {/* Earnings detail */}
        <Card className="p-6 mb-6" data-testid="earnings-card">
          <div className="flex items-center gap-2 mb-5">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            <h2 className="text-lg font-semibold">Earnings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total earned</p>
              <p className="text-3xl font-semibold tracking-tight text-emerald-500" data-testid="earnings-total">€{data.total_earned.toFixed(2)}</p>
              <p className="text-[11px] text-muted-foreground mt-1">From {data.commissions_count.total} commission(s) so far</p>
            </div>
            <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Pending payout</p>
              <p className="text-3xl font-semibold tracking-tight text-amber-500" data-testid="earnings-pending">€{data.pending_payout.toFixed(2)}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Paid monthly on the 1st</p>
            </div>
            <div className="rounded-xl bg-muted/30 border border-border p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Paid out</p>
              <p className="text-3xl font-semibold tracking-tight" data-testid="earnings-paid">€{data.paid_out.toFixed(2)}</p>
              <p className="text-[11px] text-muted-foreground mt-1">All-time</p>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                <span>Monthly commissions earned</span>
              </div>
              <span className="font-medium tabular-nums">{data.commissions_count.monthly} × €{data.rates.monthly_per_payment.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                <span>Yearly commissions earned</span>
              </div>
              <span className="font-medium tabular-nums">{data.commissions_count.yearly} × €{data.rates.yearly_per_payment.toFixed(2)}</span>
            </div>
          </div>
        </Card>

        {/* Payout details */}
        <PayoutCard earnsCash={!!data.earns_cash} isPro={!!data.is_pro} hasPaypal={!!data.has_paypal} />

        {/* Payout History — every time admin marked you as paid */}
        <PayoutHistoryCard />

        {/* How it works */}
        <Card className="p-6" data-testid="how-it-works-card">
          <h2 className="text-lg font-semibold mb-4">How payments work</h2>
          <ol className="space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center flex-shrink-0">1</span>
              <span>Share your link. We track every signup that comes through it.</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center flex-shrink-0">2</span>
              <span>When a referral upgrades to Pro, you earn — every payment they make.</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
              <span>Pending payout balance is sent to you via your registered payout method on the 1st of each month.</span>
            </li>
          </ol>
          <div className="mt-5 pt-5 border-t border-border">
            <Link to="/settings#payouts" className="text-sm text-primary hover:underline flex items-center gap-1">
              Update your payout details <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card>
      </main>
    </div>
  );
};


const PayoutCard = ({ earnsCash, isPro, hasPaypal }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');

  useEffect(() => {
    api.getPayoutInfo()
      .then(res => {
        const p = res.payout_info || {};
        setInfo(p);
        if (p.paypal_email) setPaypalEmail(p.paypal_email);
      })
      .catch(() => setInfo({}))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.updatePayoutInfo({ method: 'paypal', paypal_email: paypalEmail });
      setInfo(res.payout_info);
      toast.success('PayPal email saved — referral link is now live!');
      // Refresh parent so the banner flips to "✓ You're earning"
      try { window.location.reload(); } catch (_) { /* noop */ }
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card className="p-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></Card>;
  }

  const isConfigured = !!info?.paypal_email;

  return (
    <Card className={`p-6 mb-6 ${!isConfigured ? 'border-2 border-amber-500/30 bg-amber-500/[0.03]' : ''}`} data-testid="payout-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">PayPal Payout</h2>
        </div>
        {isConfigured ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full">
            <Check className="w-3 h-3" /> Set
          </span>
        ) : (
          <span className="text-xs font-medium text-amber-600">Required to earn</span>
        )}
      </div>

      <p className="text-sm text-muted-foreground mb-3">
        We pay exclusively via PayPal Mass Pay — make sure your PayPal email is correct.
      </p>

      {/* Eligibility banner */}
      {earnsCash ? (
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 p-3 mb-5">
          <p className="text-[12px] text-emerald-900 dark:text-emerald-100">
            <strong>✓ You're earning.</strong> Your referral link is active, commissions accrue automatically.
          </p>
        </div>
      ) : !isPro ? (
        <div className="rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40 p-3 mb-5">
          <p className="text-[12px] text-purple-900 dark:text-purple-100">
            <strong>Pro required.</strong> Only Pro members earn commissions. Free users still get signups tracked, but to convert those into cash, <Link to="/pricing" className="underline font-semibold">upgrade to Pro</Link>.
          </p>
        </div>
      ) : (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 p-3 mb-5">
          <p className="text-[12px] text-amber-900 dark:text-amber-100">
            <strong>Add PayPal to start earning.</strong> Without it, signups are still tracked but no commission is accrued. Once added, your referral link activates instantly.
          </p>
        </div>
      )}

      <div className="space-y-2 mb-5">
        <Label htmlFor="paypal-email">PayPal email <span className="text-red-500">*</span></Label>
        <Input id="paypal-email" type="email" value={paypalEmail}
          onChange={(e) => setPaypalEmail(e.target.value)}
          placeholder="you@example.com" data-testid="paypal-email-input" />
        <p className="text-[11px] text-muted-foreground">
          Must be a verified PayPal account.
        </p>
      </div>

      <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-payout-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
        Save PayPal email
      </Button>

      <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
        Payouts are processed monthly for any pending balance ≥ €10. Details are encrypted and only used for payouts.
      </p>
    </Card>
  );
};


export default AffiliateDashboard;


const PayoutHistoryCard = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getPayoutHistory()
      .then(setHistory)
      .catch(() => setHistory({ rows: [], total_paid_lifetime: 0, payout_count: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="p-6 mb-6 flex items-center justify-center" data-testid="payout-history-card">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  return (
    <Card className="p-6 mb-6" data-testid="payout-history-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-500" />
          <h2 className="text-lg font-semibold">Payout History</h2>
        </div>
        {history?.payout_count > 0 && (
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">€{history.total_paid_lifetime.toFixed(2)}</span> paid across {history.payout_count} payout(s)
          </p>
        )}
      </div>

      {!history?.rows?.length ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="payout-history-empty">
          No payouts yet. They&apos;ll appear here once your pending balance is sent to your PayPal.
        </div>
      ) : (
        <div className="space-y-2" data-testid="payout-history-list">
          {history.rows.map((p, i) => (
            <div key={i} className="rounded-lg border bg-card p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`payout-history-row-${i}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold tabular-nums text-emerald-600">€{p.amount.toFixed(2)}</p>
                  <Badge variant="outline" className="text-[10px]">{p.commission_count} commission(s)</Badge>
                </div>
                {p.note ? (
                  <p className="text-xs mt-1.5 rounded-md bg-muted/40 px-2 py-1 inline-block">
                    <span className="text-muted-foreground">Note from LinkMonkey:</span> {p.note}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground/60 italic mt-1.5">No note left</p>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground shrink-0">
                {p.paid_at ? new Date(p.paid_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
